// Kişisel iletişim bilgileri: telefon, ev adresi, acil durum kişileri (spec 18
// `feat_hris_self_service`, E32).
//
// Neden `api.ts`'e eklenmedi: `POST/PATCH /v1/employees` bu alanları bilerek
// TAŞIMIYOR. Kişisel bilgi ayrı bir kaynak, ayrı bir denetim satırı ve ayrı bir
// yetki katmanı: kişi kendi kaydını düzenler (`/v1/me/personal-info`), İK
// katmanı başkasınınkini (`/v1/employees/:id/personal-info`). TCKN ve maaş IBAN'ı
// burada YOK, ikisi de bordronundur (spec 69) ve uç onları 400 ile reddeder.
//
// Server contract: services/platform-graph/src/routes/employee-personal-info.ts.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

export interface Address {
  readonly line1: string;
  readonly line2: string | null;
  readonly city: string;
  readonly state_province: string | null;
  readonly postal_code: string | null;
  /** ISO 3166-1 alpha-2, büyük harf. */
  readonly country: string;
}

export interface EmergencyContact {
  readonly name: string;
  readonly relationship: string;
  readonly phone: string;
  readonly email: string | null;
}

export interface PersonalInfo {
  readonly employee_id: string;
  readonly primary_phone: string | null;
  readonly home_address: Address | null;
  readonly emergency_contacts: readonly EmergencyContact[];
}

/** Kişinin kendi görünümü: bordronun hesabı SALT OKUNUR ve maskeli gelir. */
export interface SelfPersonalInfo extends PersonalInfo {
  readonly payroll_bank_account: {
    readonly iban_masked: string;
    readonly bank_name: string | null;
  } | null;
}

/** Yazma gövdesi: yalnız verilen anahtarlar yazılır, `null` telefonu/adresi siler. */
export interface PersonalInfoPatch {
  readonly primary_phone?: string | null;
  readonly home_address?: {
    readonly line1: string;
    readonly line2?: string;
    readonly city: string;
    readonly state_province?: string;
    readonly postal_code?: string;
    readonly country: string;
  } | null;
  readonly emergency_contacts?: readonly {
    readonly name: string;
    readonly relationship: string;
    readonly phone: string;
    readonly email?: string;
  }[];
}

/** Uçtaki sınırın aynısı: bundan fazlası formda değil, sunucuda 400 olurdu. */
export const MAX_EMERGENCY_CONTACTS = 5;

/**
 * `["employees"]` altında durur: bir kişiyi düzenlemek o öneki zaten geçersiz
 * kılıyor ve kişisel bilgi de o kişinin bir parçası.
 */
export const PERSONAL_INFO_KEY = ["employees", "personal-info"] as const;

type SelfGet = (path: "/v1/me/personal-info") => Promise<EnvelopedFetchResult<SelfPersonalInfo>>;
type SelfPatch = (
  path: "/v1/me/personal-info",
  init: { body: PersonalInfoPatch },
) => Promise<EnvelopedFetchResult<SelfPersonalInfo>>;
type HrGet = (
  path: "/v1/employees/{id}/personal-info",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<PersonalInfo>>;
type HrPatch = (
  path: "/v1/employees/{id}/personal-info",
  init: { params: { path: { id: string } }; body: PersonalInfoPatch },
) => Promise<EnvelopedFetchResult<PersonalInfo>>;

export async function fetchMyPersonalInfo(): Promise<SelfPersonalInfo> {
  const get = platformGraph.GET as unknown as SelfGet;
  return unwrapEnvelope(get("/v1/me/personal-info"));
}

export async function updateMyPersonalInfo(body: PersonalInfoPatch): Promise<SelfPersonalInfo> {
  const patch = platformGraph.PATCH as unknown as SelfPatch;
  return unwrapEnvelope(patch("/v1/me/personal-info", { body }));
}

export async function fetchEmployeePersonalInfo(id: string): Promise<PersonalInfo> {
  const get = platformGraph.GET as unknown as HrGet;
  return unwrapEnvelope(get("/v1/employees/{id}/personal-info", { params: { path: { id } } }));
}

export async function updateEmployeePersonalInfo(
  id: string,
  body: PersonalInfoPatch,
): Promise<PersonalInfo> {
  const patch = platformGraph.PATCH as unknown as HrPatch;
  return unwrapEnvelope(
    patch("/v1/employees/{id}/personal-info", { params: { path: { id } }, body }),
  );
}
